/**
 * 视频卡片组件 - 复用Home页面的vcard结构
 * 用于在不同页面显示视频，支持不同的操作模式
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { VideoDescriptor } from '../types/api';
import { getVideoCoverUrl, handleCoverError } from '../utils/videoCover';

// 语言代码到中文名称的映射
const getLanguageName = (langCode: string): string => {
  const languageNames: Record<string, string> = {
    'zh': '中文',
    'en': '英语',
    'ja': '日语',
    'ko': '韩语',
    'es': '西班牙语',
    'fr': '法语',
    'de': '德语',
    'ru': '俄语',
  };
  return languageNames[langCode] || langCode;
};

interface VideoCardProps {
  video: VideoDescriptor;
  mode?: 'home' | 'management' | 'history';
  onEdit?: (videoId: string) => void;
  onDelete?: (videoId: string) => void;
  showAdminActions?: boolean;
}

export const VideoCard: React.FC<VideoCardProps> = ({
  video,
  mode = 'home',
  onEdit,
  onDelete,
  showAdminActions = false,
}) => {
  const navigate = useNavigate();


  const handleVideoClick = () => {
    if (video.playable) {
      const from = mode === 'management' ? 'management' : mode === 'history' ? 'history' : 'list';
      const navigationState: any = {
        initialTitle: video.name || video.videoId,
      };

      // 如果是从浏览历史进入，传递观看进度
      if (mode === 'history' && video._meta?.watchProgress !== undefined) {
        navigationState.initialProgress = video._meta.watchProgress;
      }

      navigate(`/player?video=${encodeURIComponent(video.videoId)}&from=${from}`, {
        state: navigationState,
      });
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(video.videoId);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(video.videoId);
  };

  return (
    <div
      className="vcard"
      onClick={handleVideoClick}
    >
      {/* 视频区 - 承载图片和所有角标 */}
      <div className="vcard__thumb">
        <img
          src={getVideoCoverUrl(video)}
          alt={video.name || video.videoId}
          onError={handleCoverError}
          loading="lazy"
          decoding="async"
        />
        <div className="thumbnail-placeholder" style={{ display: 'none' }}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </div>

        {/* 右上角标 */}
        {video.capabilities.hasWordAlignment && (
          <div className="video-badge">
            <span className="icon-inline" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2 5 5 .5-4 3 1 5-4-2-4 2 1-5-4-3 5-.5z"></path></svg></span>
            <span>词级对齐</span>
          </div>
        )}

        {/* 右下时长 */}
        {video._meta?.durationFormatted && video._meta.durationFormatted !== '未知' && (
          <div className="video-duration">
            <span>{video._meta.durationFormatted}</span>
          </div>
        )}

        {/* 观看进度指示 - 只在浏览历史模式显示 */}
        {mode === 'history' && video._meta?.watchProgress !== undefined && (
          <>
            {video._meta.watchProgress === 0 && (
              <div className="video-watch-status video-watch-status--unwatched">
                <span>未观看</span>
              </div>
            )}
            {video._meta.watchProgress > 0 && video._meta.watchProgress < 100 && (
              <div className="video-progress-bar">
                <div
                  className="video-progress-fill"
                  style={{ width: `${video._meta.watchProgress}%` }}
                />
              </div>
            )}
            {video._meta.watchProgress === 100 && (
              <div className="video-watch-status video-watch-status--completed">
                <span>已看完</span>
              </div>
            )}
          </>
        )}

        {/* 左下观看次数 */}
        {video._meta?.viewCount !== undefined && (
          <div className="video-view-count">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <span>{video._meta.viewCount}</span>
          </div>
        )}

      </div>

      {/* 标题区 - 固定高度容器 */}
      <div
        className="vcard__title"
        data-text={video.name || video.videoId}
      />

      {/* 标签区 - 语言和观看数标签 */}
      <div className="vcard__tags">
        {video.sourceLanguage && (
          <span className="vcard__tag vcard__tag--lang">
            {getLanguageName(video.sourceLanguage)}
          </span>
        )}
        {/* 其他标签可以在这里添加 */}

        {/* 管理功能按钮 - 只在management模式显示 */}
        {mode === 'management' && showAdminActions && (
          <div className="vcard__admin-actions">
            <button
              className="vcard__admin-action vcard__admin-action--edit"
              onClick={handleEditClick}
              title="编辑视频"
              aria-label="编辑视频"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <button
              className="vcard__admin-action vcard__admin-action--delete"
              onClick={handleDeleteClick}
              title="删除视频"
              aria-label="删除视频"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
